/**
 * Centralized campaign status display logic.
 *
 * The DB stores a lifecycle status (DRAFT, PUBLISHED, ACTIVE, ENDED, ARCHIVED),
 * but the user-facing label for PUBLISHED campaigns depends on the current time
 * relative to startAt / endAt:
 *
 *   - Before startAt  → "Upcoming"
 *   - Between start/end (or no dates) → "In Progress"
 *   - After endAt     → "Ended"
 *
 * Other statuses map 1-to-1 to their display labels.
 */

export type DisplayStatus =
  | "Draft"
  | "Upcoming"
  | "In Progress"
  | "Ended"
  | "Archived";

interface CampaignTimestamps {
  status: string;
  startAt?: string | Date | null;
  endAt?: string | Date | null;
}

/**
 * Returns the user-facing display label for a campaign.
 */
export function getDisplayStatus(campaign: CampaignTimestamps): DisplayStatus {
  const { status, startAt, endAt } = campaign;

  if (status === "DRAFT") return "Draft";
  if (status === "ARCHIVED") return "Archived";
  if (status === "ENDED") return "Ended";

  // PUBLISHED or ACTIVE — resolve based on time
  if (status === "PUBLISHED" || status === "ACTIVE") {
    const now = new Date();
    const start = startAt ? new Date(startAt) : null;
    const end = endAt ? new Date(endAt) : null;

    if (end && now > end) return "Ended";
    if (start && now < start) return "Upcoming";
    return "In Progress";
  }

  // Fallback for unknown statuses
  return status as DisplayStatus;
}

/** Standard badge colors (light backgrounds). */
export function getStatusColor(displayStatus: DisplayStatus): string {
  switch (displayStatus) {
    case "Draft":
      return "bg-gray-100 text-gray-800";
    case "Upcoming":
      return "bg-blue-100 text-blue-800";
    case "In Progress":
      return "bg-green-100 text-green-800";
    case "Ended":
      return "bg-red-100 text-red-800";
    case "Archived":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}

/** Hero/overlay badge colors (semi-transparent with white text). */
export function getStatusColorHero(displayStatus: DisplayStatus): string {
  switch (displayStatus) {
    case "Draft":
      return "bg-gray-500/90 text-white";
    case "Upcoming":
      return "bg-blue-500/90 text-white";
    case "In Progress":
      return "bg-green-500/90 text-white";
    case "Ended":
      return "bg-red-500/90 text-white";
    case "Archived":
      return "bg-gray-500/90 text-white";
    default:
      return "bg-gray-500/90 text-white";
  }
}

/**
 * The set of display statuses that are useful as filter options on public pages.
 * DRAFT is excluded since it's admin-only.
 */
export const FILTERABLE_STATUSES: { value: DisplayStatus; label: string }[] = [
  { value: "Upcoming", label: "Upcoming" },
  { value: "In Progress", label: "In Progress" },
  { value: "Ended", label: "Ended" },
];
